import isNil from 'lodash/isNil';
import isEmpty from 'lodash/isEmpty';
import {
  areThereAnyErrors,
  validateEventConditions,
  validateExpandEventConditions,
  validateFoldEventConditions,
  validateMergeEventsConditions,
} from '../../../components/Builder/utils/validateExecutionInput';
import { DocumentNode } from 'graphql';
import {
  CohortSampleOperationKey,
  QueryType,
} from '../../apollo/graphqlSchema';
import { IBuilder, IRow, RowType } from '../../../redux/slices/oldBuilder';
import { isEmptyEventCondition } from '../../builder';
import {
  MAX_PREDICTION_TIME_WINDOW,
  MAX_TIME_WINDOW,
  MIN_PREDICTION_TIME_WINDOW,
  ROWS_COUNT_ALLOWS_COMPARE_OR,
} from '../../../config/constants';
import { isValidNumberWithinRange } from '../../numbers';
import { getBasicMeasureRowErrors } from '../../builder/validation';
import { getCohortRowErrorsByQueryType } from '../../../components/Builder/utils/getCohortRowErrorsByQueryType';
import validateQueryBuilder from '../../../components/Builder/utils/query/validateQueryBuilder';
import { assertCondition } from '../../types';
import {
  EXECUTE_COHORT,
  EXECUTE_DATA_TABLE,
  EXECUTE_FUNNEL,
  EXECUTE_PATH,
  EXECUTE_PREDICTION,
  EXECUTE_QUERY,
  EXECUTE_RETENTION,
} from '../../apollo/analysis/analysisExecute';
import validateFunnelBuilder from '../../../components/Builder/utils/funnel/validateFunnelBuilder';
import { UnreachableCaseError } from 'ts-essentials';

export const getMutationByQueryType: Record<
  Exclude<QueryType, QueryType.Significance>,
  DocumentNode
> = {
  [QueryType.Expression]: EXECUTE_QUERY,
  [QueryType.Funnel]: EXECUTE_FUNNEL,
  [QueryType.Retention]: EXECUTE_RETENTION,
  [QueryType.Prediction]: EXECUTE_PREDICTION,
  [QueryType.Path]: EXECUTE_PATH,
  [QueryType.CohortSample]: EXECUTE_COHORT,
  [QueryType.DataTable]: EXECUTE_DATA_TABLE,
  // Significance is handled separately
};

const checkRetentionTimeWindow = (builderState: IBuilder) => {
  const timeWindow = builderState.timeWindow;

  if (
    !isNil(timeWindow) &&
    !isValidNumberWithinRange(timeWindow, 0, MAX_TIME_WINDOW)
  ) {
    return false;
  }

  return true;
};

const checkPredictionTimeWindow = (builderState: IBuilder['prediction']) => {
  const timeWindow = builderState.timeWindow;

  if (
    !isNil(timeWindow) &&
    !isValidNumberWithinRange(
      timeWindow,
      MIN_PREDICTION_TIME_WINDOW,
      MAX_PREDICTION_TIME_WINDOW,
    )
  ) {
    return false;
  }

  return true;
};

const checkCohortRows = (builderState: IBuilder, queryType: QueryType) => {
  if (queryType === QueryType.CohortSample) {
    const cohortRows = builderState[RowType.builderCohorts];
    const builderSampledCohort = builderState.builderSampledCohort;
    const { sampleOperation } = builderSampledCohort;

    if (
      sampleOperation?.key === CohortSampleOperationKey.Compare &&
      cohortRows.length !== ROWS_COUNT_ALLOWS_COMPARE_OR
    ) {
      return false;
    }
  }

  const errors = getCohortRowErrorsByQueryType(builderState, queryType);

  return !areThereAnyErrors(errors);
};

const checkDataTableMeasureRows = (rows: IRow[]) => {
  return rows.every((row) => {
    // valid if the row contains a measureId
    if (row.measureId?.length) {
      return true;
    }

    const errors = getBasicMeasureRowErrors(row);
    return isEmpty(errors);
  });
};

const checkPredictionExecuteStatus = (prediction: any) => {
  if (isEmptyEventCondition(prediction.eventCondition)) {
    return false;
  }
  if (!isNaN(prediction.timeWindow)) {
    const timeWindowNumber = Number(prediction.timeWindow);
    if (timeWindowNumber <= 0 || timeWindowNumber > 14) {
      return false;
    }
  }

  return true;
};

export const validateFormula = (
  queryType: QueryType,
  builderState: IBuilder,
): boolean => {
  assertCondition(queryType !== QueryType.Significance);

  switch (queryType) {
    case QueryType.Expression: {
      const errors = validateQueryBuilder(builderState);

      return isEmpty(errors);
    }
    case QueryType.DataTable:
      return (
        checkDataTableMeasureRows([...builderState.dataTableMeasures]) &&
        checkCohortRows(builderState, queryType)
      );
    case QueryType.Funnel: {
      const errors = validateFunnelBuilder(builderState);

      return isEmpty(errors);
    }
    case QueryType.Retention:
      assertCondition(
        builderState.selectedOptions?.type === QueryType.Retention,
      );
      return (
        checkRetentionTimeWindow(builderState) &&
        checkCohortRows(builderState, queryType) &&
        validateEventConditions(
          builderState.retention.retentionConditions[0],
        ) &&
        validateEventConditions(builderState.retention.retentionConditions[1])
      );
    case QueryType.Prediction:
      return (
        checkPredictionTimeWindow(builderState.prediction) &&
        checkPredictionExecuteStatus(builderState.prediction) &&
        checkCohortRows(builderState, queryType)
      );
    case QueryType.Path:
      return (
        validateEventConditions(builderState.paths[0]) &&
        validateEventConditions(builderState.paths[1]) &&
        validateMergeEventsConditions(builderState.mergeEvents) &&
        validateExpandEventConditions(builderState.expandEvents) &&
        validateFoldEventConditions(builderState.foldEvents) &&
        checkCohortRows(builderState, queryType)
      );
    case QueryType.CohortSample:
      return checkCohortRows(builderState, queryType);
    default:
      throw new UnreachableCaseError(queryType);
  }
};

export const getExecutionResponseByType: Record<
  Exclude<QueryType, QueryType.Significance>,
  string
> = {
  [QueryType.Expression]: 'data.queryExecute',
  [QueryType.DataTable]: 'data.dataTableExecute',
  [QueryType.Funnel]: 'data.funnelExecute',
  [QueryType.Retention]: 'data.retentionExecute',
  [QueryType.Prediction]: 'data.predictionExecute',
  [QueryType.Path]: 'data.pathExecute',
  [QueryType.CohortSample]: 'data.cohortExecute',
  // Significance is handled separately
};
